import Select from 'react-select';
import { useState, useEffect } from 'react';
import Joyride, { ACTIONS, CallBackProps, STATUS } from 'react-joyride';

import Button from 'components/Button';
import Header from 'components/Header';
import AboutModal from 'components/AboutModal';
import WarningIcon from 'assets/icons/warning.svg';
import resetGrayIcon from 'assets/icons/reset-gray.svg';
import ArrowBackIcon from 'assets/icons/arrow-back.svg';
import { AnimatedLoading } from 'components/AnimatedLoading';
import ConfirmPortCoModal from 'components/ConfirmPortCoModal';
import ArrowBackWhiteIcon from 'assets/icons/arrow-back-white.svg';
import { PortCoUserDataProps } from 'components/EditableTable/types';
import LoadingComponent from 'components/SectionCharts/LoadingComponent';
import { SELECT_CLASSNAMES } from 'components/EditableTable/utils/constants';
import CustomWalkthroughTooltip from 'components/CustomWalkthroughTooltip';
import {
  nonReviewedPortCoWalkthroughSteps,
  reviewedPortCoWalkthroughSteps
} from 'utils/constants/tutorials-walkthrough';
import ErrorPortCoModal from 'components/ErrorPortCoModal';

const PortCoUserData = ({
  data,
  isDarkMode,
  isReviewed,
  loadingData,
  isFormDirty,
  initialData,
  handleSubmit,
  renderSection,
  handleNavigate,
  loadingHistory,
  historyOptions,
  handleResetData,
  selectedVersion,
  isAboutModalOpen,
  isErrorModalOpen,
  toggleAboutModal,
  handleUpdateData,
  isSubmitModalOpen,
  loadingSubmission,
  handleCloseErrorModal,
  handleSelectedVersion
}: PortCoUserDataProps) => {
  const walkthroughSteps = isReviewed
    ? reviewedPortCoWalkthroughSteps
    : nonReviewedPortCoWalkthroughSteps;

  const [walkthroughActive, setWalkthroughActive] = useState(false);

  useEffect(() => {
    const skipTutorial = localStorage.getItem(
      isReviewed
        ? 'skip_reviewed_portco_tutorial'
        : 'skip_non_reviewed_portco_tutorial'
    );
    if (!skipTutorial && !loadingData) {
      setWalkthroughActive(true);
    }
  }, [loadingData, isReviewed]);

  const handleWalkthroughCallback = (data: CallBackProps) => {
    if (data.action === ACTIONS.SKIP) {
      localStorage.setItem(
        isReviewed
          ? 'skip_reviewed_portco_tutorial'
          : 'skip_non_reviewed_portco_tutorial',
        'true'
      );
      setWalkthroughActive(false);
    }
    if (data.status === STATUS.FINISHED) {
      localStorage.setItem(
        isReviewed
          ? 'skip_reviewed_portco_tutorial'
          : 'skip_non_reviewed_portco_tutorial',
        'true'
      );
      setWalkthroughActive(false);
    }
  };

  return (
    <div className='App dark:text-white dark:bg-black'>
      <main className='App-header max-w-[1660px] mx-auto'>
        <Header toggleAboutModal={toggleAboutModal} />

        <div className='flex flex-col gap-6 mt-4 p-4 pb-10'>
          <div className='flex items-center justify-between gap-4'>
            <div className='flex items-center gap-6'>
              {isReviewed && (
                <a
                  href='/'
                  className='flex items-center justify-center w-10 h-10 rounded-full bg-black dark:bg-white opacity-100 hover:opacity-70 cursor-pointer transition-all duration-300'
                >
                  <img
                    alt='back'
                    src={isDarkMode ? ArrowBackIcon : ArrowBackWhiteIcon}
                  />
                </a>
              )}

              <div>
                <p className='font-semibold text-[32px] '>
                  Review and update your data
                </p>

                <p className='mt-2 font-light text-base text-[#5C5C5C]'>
                  Please review and confirm the data below based on board
                  materials, financials, and discussions between ICONIQ and your
                  company.
                </p>
              </div>
            </div>

            <div className='flex items-center gap-4'>
              {isFormDirty && (
                <Button
                  id='reset'
                  label='Reset Data'
                  onClick={handleResetData}
                  prefix={<img src={resetGrayIcon} alt='reset' />}
                  className='flex items-center gap-2 px-4 py-2 rounded-5xl text-base font-light text-center text-[#9B9B9B] dark:hover:bg-[#EEEEEE10] hover:bg-[#eee] transition-all duration-300'
                />
              )}

              {!isReviewed ? (
                <Button
                  id='confirm'
                  onClick={handleUpdateData}
                  disabled={loadingSubmission || !initialData.length}
                  label={
                    loadingSubmission ? (
                      <AnimatedLoading label='Saving' />
                    ) : isFormDirty ? (
                      'Update & Confirm Data'
                    ) : (
                      'Confirm Data'
                    )
                  }
                  className='px-8 py-2 rounded-5xl text-lg font-light text-center text-white bg-black dark:bg-white dark:text-black hover:bg-[#404040] dark:hover:bg-gray-400 transition-all duration-300 disabled:opacity-70 disabled:hover:opacity-70 disabled:bg-black disabled:dark:bg-white disabled:cursor-not-allowed walkthrough-confirm'
                />
              ) : (
                <>
                  <Select
                    isClearable
                    isSearchable={false}
                    placeholder='History'
                    value={selectedVersion}
                    options={historyOptions}
                    isLoading={loadingHistory}
                    classNames={SELECT_CLASSNAMES}
                    onChange={handleSelectedVersion}
                    noOptionsMessage={() => 'No History Yet!'}
                    loadingMessage={() => (
                      <LoadingComponent className='h-auto' />
                    )}
                  />

                  <Button
                    id='update'
                    label={
                      loadingSubmission ? (
                        <AnimatedLoading label='Saving' />
                      ) : (
                        'Update Data'
                      )
                    }
                    onClick={handleUpdateData}
                    disabled={loadingSubmission || !isFormDirty}
                    className={`px-8 py-2 rounded-5xl text-lg font-light text-center text-white dark:text-black transition-all duration-300 walkthrough-update-data ${
                      isFormDirty && !loadingSubmission
                        ? '!text-black !bg-[#6AD487] hover:bg-[#6AD487] hover:opacity-70'
                        : 'opacity-60 bg-black dark:bg-white hover:dark:bg-white hover:bg-[#404040] !cursor-not-allowed'
                    }`}
                  />
                </>
              )}
            </div>
          </div>

          <div className='flex items-center gap-10 mt-4 mb-2'>
            <div className='flex items-center justify-between gap-2 font-light text-[#5C5C5C]'>
              <div className='w-8 h-8 rounded-lg border border-[#DDDDDD]' />
              Editable field
            </div>

            <div className='flex items-center justify-between gap-2 font-light text-[#5C5C5C]'>
              <div className='w-8 h-8 rounded-lg border border-[##D5F0DD] bg-[#D5F0DD]' />
              Updated Data (and cross-linked fields)
            </div>

            <div className='flex items-center justify-between gap-2 font-light text-[#5C5C5C]'>
              <div className='w-8 h-8 rounded-lg border border-[#DDDDDD] bg-[#DDDDDD] dark:bg-gray-850' />
              Locked field (calculations)
            </div>

            <div className='flex items-center justify-between gap-2 font-light text-[#5C5C5C]'>
              <div className='flex items-center justify-center w-8 h-8 rounded-lg border border-[##FDF1F1] bg-[#FDF1F1]'>
                <img src={WarningIcon} alt='warning' />
              </div>
              Potentially Invalid Data
            </div>
          </div>

          {loadingData ? (
            <LoadingComponent />
          ) : (
            <>
              {renderSection('Key Financials', data.slice(0, 32), 0)}
              {renderSection('Growth & Retention', data.slice(32, 40), 1)}
              {renderSection('Operational Efficiency', data.slice(40, 49), 2)}
              {renderSection(
                'Profitability and Cash Flow',
                data.slice(49, 51),
                3
              )}
              {renderSection('Headcount', data.slice(51), 4)}
            </>
          )}
        </div>
      </main>

      <AboutModal isOpen={isAboutModalOpen} onClose={toggleAboutModal} />

      <ConfirmPortCoModal isOpen={isSubmitModalOpen} onClose={handleNavigate} />

      <ErrorPortCoModal
        onSubmit={handleSubmit}
        isOpen={isErrorModalOpen}
        onClose={handleCloseErrorModal}
      />

      <Joyride
        continuous
        disableOverlayClose
        scrollDuration={1000}
        run={walkthroughActive}
        locale={{ last: 'Finish' }}
        steps={walkthroughSteps}
        callback={handleWalkthroughCallback}
        styles={{
          options: { arrowColor: 'white' },
          overlay: { width: '100%' }
        }}
        tooltipComponent={CustomWalkthroughTooltip}
      />
    </div>
  );
};

export default PortCoUserData;
